import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { isEmpty, extractErrors, notifySuccess } from '../../Utility'
import { cloneDeep } from 'lodash'
import * as actions from '../../../../actions'
import getText from '../../i18n/labels'

// FORM
import { BwmCheckbox } from '../BwmCheckboxB5'
import { BwmInput } from '../BwmInputB5'
import { BwmSelect } from '../BwmSelectB5'
import { Formik, Form, useFormikContext } from 'formik'
import * as Yup from 'yup'
import ErrorListAlert from '../ErrorListAlert'

// COMPONENTS
import { PopupError } from '../../popups/PopupError'
import { PageSpinner } from '../../spinner/PageSpinner'
import { InfoBox } from '../../InfoBox'
import { PopupAlert } from '../../popups/PopupAlert'

const onlyNumbers = new RegExp('^d+$')

/**
 * Form per la gestione dei parametri PEC per un soggetto CORA o CRS
 *
 * @param {String} coraSubjectSid - cora subject SID
 * @param {String} subjectFiscalCode - subject fiscal code
 * @param {Object} user - user
 *
 * @see https://github.com/jquense/yup
 * @see https://github.com/jaredpalmer/formik
 *
 */
export function FormPecParameters(props) {
  const lang = useSelector((state) => state.language.language)
  const labels = getText(lang)

  const coraSubjectSid = props.coraSubjectSid
  const subjectFiscalCode = props.subjectFiscalCode
  const user = useSelector((state) => state.auth.customer.user)

  const [showErrorAlert, setShowErrorAlert] = useState(false)
  const [initialValues, setInitialValues] = useState({ needsToAccept: false })

  const [mailPresets, setMailPresets] = useState([])
  const [comureiSubjectPec, setComureiSubjectPec] = useState(null)
  const [loading, setLoading] = useState(false)
  const [needsTest, setNeedsTest] = useState(true)

  useEffect(() => {
    actions.getMailPresets().then((res) => setMailPresets(res))
    if (subjectFiscalCode) {
      actions.getPecByFiscalCode(subjectFiscalCode).then((res) => setComureiSubjectPec(res))
    }
  }, [])

  useEffect(() => {
    const form = cloneDeep(initialValues)
    form.emailAddress = coraSubjectSid?.pecEmail || ''
    form.password = coraSubjectSid?.mailPassword || ''
    form.inFolder = coraSubjectSid?.incomingMailFolder || 'INBOX'
    form.contactEmail = coraSubjectSid?.contactEmail || user.email || ''
    form.recipientEmail = coraSubjectSid?.recipientEmail || 'SID1@PCERT.AGENZIAENTRATE.IT'
    form.outgoingMailHost = coraSubjectSid?.outgoingMailHost || ''
    form.outgoingMailPort = coraSubjectSid?.outgoingMailPort || ''
    form.incomingMailHost = coraSubjectSid?.incomingMailHost || ''
    form.incomingMailPort = coraSubjectSid?.incomingMailPort || ''
    form.automaticElaborationEnabled =
      coraSubjectSid?.automaticElaborationEnabled === false ? false : true
    setInitialValues(form)
  }, [coraSubjectSid])

  useEffect(() => {
    if (
      mailPresets.length > 0 &&
      comureiSubjectPec &&
      comureiSubjectPec.pecManager &&
      isEmpty(coraSubjectSid?.pecEmail)
    ) {
      const form = cloneDeep(initialValues)
      form.mailPresets = comureiSubjectPec.pecEmail ? comureiSubjectPec.pecEmail.trim() : ''
      setInitialValues(form)
    }
  }, [mailPresets, comureiSubjectPec])

  /**
   * Schema di validazione per i campi del form
   *
   */
  const validationSchema = Yup.object().shape({
    // tutti i campi sono obbligatori
    emailAddress: Yup.string()
      .required(labels.REQUIRED_FIELD)
      .email(labels.INCORRECT_EMAIL_ADDRESS),
    recipientEmail: Yup.string()
      .required(labels.REQUIRED_FIELD)
      .email(labels.INCORRECT_EMAIL_ADDRESS),
    password: Yup.string().required(labels.REQUIRED_FIELD),
    incomingMailHost: Yup.string().required(labels.REQUIRED_FIELD),
    incomingMailPort: Yup.number().required(labels.REQUIRED_FIELD),
    outgoingMailHost: Yup.string().required(labels.REQUIRED_FIELD),
    outgoingMailPort: Yup.number().required(labels.REQUIRED_FIELD),
    inFolder: Yup.string().required(labels.REQUIRED_FIELD),
    // Bisogna accettare le condizioni
    needsToAccept: Yup.boolean().oneOf([true], labels.YOU_MUST_ACCEPT_THE_CONDITIONS)
  })

  /**
   * Serve per mappare il nome del campo del form con la label da mostrare in caso di errore nell'alert
   * errorKey: 'nome del campo del form', errorLabel: 'label da mostrare quando si verifica l'errore'
   *
   */
  const formErrorsTabMapping = [
    {
      errorKey: 'emailAddress',
      errorLabel: labels.EMAIL_ADDRESS_TO_RECEIVE_GENERATED_FROM
    },
    {
      errorKey: 'password',
      errorLabel: labels.PASSWORD
    },
    {
      errorKey: 'inFolder',
      errorLabel: labels.INCOMING_MAIL_FOLDER
    },
    {
      errorKey: 'contactEmail',
      errorLabel: labels.CONTACT_EMAIL
    },
    {
      errorKey: 'recipientEmail',
      errorLabel: labels.PEC_RECIPIENT
    },
    {
      errorKey: 'outgoingMailHost',
      errorLabel: labels.OUTGOING_MAIL_HOST
    },
    {
      errorKey: 'outgoingMailPort',
      errorLabel: labels.OUTGOING_MAIL_PORT
    },
    {
      errorKey: 'incomingMailHost',
      errorLabel: labels.INCOMING_MAIL_HOST
    },
    {
      errorKey: 'incomingMailPort',
      errorLabel: labels.INCOMING_MAIL_PORT
    },
    {
      errorKey: 'automaticElaborationEnabled',
      errorLabel: labels.ELABORATE_AUTOMATICALLY_RECEIPTS
    },
    {
      errorKey: 'needsToAccept',
      errorLabel: labels.TERMS_AND_CONDITIONS
    },
    {
      errorKey: 'connection',
      errorLabel: labels.CHECK_CONNECTION
    }
  ]

  /**
   * Ritorna una lista di errori estratti dal validatore per essere stampati nell'alert.
   *
   * Se non ci sono errori e la flag needsTest è impostata su true,
   * viene aggiunto un nuovo errore all'oggetto degli errori con la chiave "connection"
   * e indica che "DEVI VERIFICARE LA CONNESSIONE PRIMA DI SALVARE".
   *
   * La funzione quindi chiama la funzione di utility extractErrors per estrarre
   * gli errori dal validatore e restituisce il risultato da passare all'alert.
   *
   * @param {Object} errors - oggetto contenente gli errori
   * @returns {Array} lista di errori da passare all'alert
   */
  const getErrors = (errors) => {
    if (!errors) errors = {}
    if (needsTest && Object.keys(errors)?.length === 0) {
      errors.connection = labels.YOU_MUST_CHECK_THE_CONNECTION_BEFORE_SAVING
    }
    return extractErrors(errors, formErrorsTabMapping)
  }

  return (
    <div key={'pec-' + coraSubjectSid?.id}>
      {loading && <PageSpinner />}
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        validationSchema={validationSchema}>
        {({ errors }) => (
          <Form>
            {showErrorAlert && (
              <div>
                {/* Allert degli errori */}
                <ErrorListAlert errors={getErrors(errors)} hide={() => setShowErrorAlert(false)} />
              </div>
            )}
            <FormBody
              {...props}
              needsTest={needsTest}
              mailPresets={mailPresets}
              coraSubjectSid={coraSubjectSid}
              setNeedsTest={setNeedsTest}
              setLoading={setLoading}
              setShowErrorAlert={setShowErrorAlert}
            />
          </Form>
        )}
      </Formik>
    </div>
  )
}

/**
 * FormBody è il contenuto del form, è stato diviso in 2 componente per poter usare useFormikContext e quindi avere una variabile values reattiva al cambio del form
 *
 * @param {boolean} archived - Se il soggetto cora è archiviato.
 * @param {Function} onCancel - La funzione da chiamare quando viene cliccato il pulsante di annulla, riporta il form allo stato iniziale (senza modifiche).
 * @param {Function} onSave - La funzione da chiamare quando viene cliccato il pulsante di salvataggio.
 * @param {Object} coraSubjectSid - Il sid del soggetto cora.
 * @param {Object} mailPresets - La lista dei preset di email.
 * @param {boolean} needsTest - Un flag per indicare se la configurazione deve essere testata.
 * @param {boolean} showErrorAlert - Un flag per indicare se mostrare l'alert degli errori.
 * @param {Function} setNeedsTest - La funzione da chiamare per settare il flag needsTest.
 * @param {Function} setShowErrorAlert - La funzione da chiamare per decidere se mostrare o no l'alert degli errori.
 *
 *  */
const FormBody = (props) => {
  const lang = useSelector((state) => state.language.language)
  const labels = getText(lang)

  const { values, errors, setValues, validateForm } = useFormikContext()
  const [touched, setTouched] = useState(false)

  const [passwordFieldType, setPasswordFieldType] = useState('password')
  const [operatorSelect, setOperatorSelect] = useState()

  /**
   * Esegue la validazione del form
   * Se non sono presenti errori o se è presente un solo errore con chiave "connection" esegue il test dei parametri email
   * Altrimenti mostra l'alert con gli errori di validazione
   *
   */
  const handleFormVerify = async () => {
    setTouched(true)
    const errors = await validateForm(values) // Validazione dei valori
    if (Object.keys(errors).length == 0 || (Object.keys(errors).length == 1 && errors.connection)) {
      props.setShowErrorAlert(false) // Nascondi l'alert
      testMailParameters(values)
    } else {
      props.setShowErrorAlert(true) // Mostra l'alert degli errori
    }
  }

  /**
   * Popola i valori del form in base al preset selezionato
   *
   * @param {Object} selected - Oggetto selezionato dal select
   */
  const handleChangeMailPreset = (selected) => {
    setOperatorSelect(selected?.id)
    const preset = props.mailPresets.find((e) => e.id == selected?.id)
    const form = cloneDeep(values)
    if (preset) {
      form.outgoingMailHost = preset.outgoingMailHost
        ? preset.outgoingMailHost.trim()
        : preset.outgoingMailHost
      form.outgoingMailPort = preset.outgoingMailPort
        ? preset.outgoingMailPort.trim()
        : preset.outgoingMailPort
      form.incomingMailHost = preset.incomingMailHost
        ? preset.incomingMailHost.trim()
        : preset.incomingMailHost
      form.incomingMailPort = preset.incomingMailPort
        ? preset.incomingMailPort.trim()
        : preset.incomingMailPort
    } else {
      form.outgoingMailHost = ''
      form.outgoingMailPort = ''
      form.incomingMailHost = ''
      form.incomingMailPort = ''
    }
    props.setNeedsTest(true)
    setValues(form)
  }

  /**
   * Abilita o disabilita l'elaborazione automatica delle e-mail
   *
   * @param {boolean} value - Valore selezionato
   */
  const handleAutoElaborationCheckBox = (value) => {
    const form = cloneDeep(values)
    form.automaticElaborationEnabled = value
    props.setNeedsTest(true)
    setValues(form)
  }

  /**
   * Accetta o rifiuta le condizioni di utilizzo della PEC
   *
   * @param {boolean} value - Valore selezionato
   */
  const handleCheckbox = (value) => {
    const form = Object.assign({}, values)
    form.needsToAccept = value
    props.setNeedsTest(true)
    setValues(form)
  }

  /**
   * Gestisce le modifiche ai campi nel form.
   *
   * Aggiorna il campo specificato nel form values con il valore dato.
   * Opzionalmente trimma il valore prima di aggiornare.
   *
   * @param {string} value - Il nuovo valore per il campo.
   * @param {string} field - Il campo nel form da aggiornare.
   * @param {boolean} [trim=true] - Se trimmare il valore prima di aggiornare.
   */

  /*******  201ab9c8-8b53-437e-b907-15eb4a368d76  *******/
  const handleChangeTextField = (value, field, trim = true) => {
    let val = value
    if (trim) val = val.trim()
    const form = Object.assign({}, values)
    form[field] = val
    props.setNeedsTest(true)
    setValues(form)
  }

  /**
   * Gestisce le modifiche ai campi numerici nel form.
   *
   * Rimuove tutti i caratteri non numerici dal valore e aggiorna il campo specificato nel form values con il valore dato.
   *
   * @param {string} value - Il nuovo valore per il campo.
   * @param {string} field - Il campo nel form da aggiornare.
   */
  const handleChangeNumericField = (value, field) => {
    const form = Object.assign({}, values)
    const val = value.replace(onlyNumbers)
    form[field] = val
    props.setNeedsTest(true)
    setValues(form)
  }

  /**
   * Gestisce il click sul pulsante occhio della password.
   *
   * Alterna il tipo del campo password tra 'password' e 'text'.
   */
  const onClickPwdEye = () => {
    if (passwordFieldType === 'password') {
      setPasswordFieldType('text')
    } else if (passwordFieldType === 'text') {
      setPasswordFieldType('password')
    }
  }

  /**
   * Pulisce la configurazione della PEC del soggetto selezionato.
   *
   * Mostra un alert di conferma per cancellare la configurazione della PEC del soggetto.
   * Se l'utente conferma, cancella la configurazione della PEC del soggetto selezionato.
   */
  const onClean = () => {
    PopupAlert({
      text: labels.DO_YOU_WANT_TO_DELETE_THE_PEC_CONFIGURATION,
      handleClickConfirm: () => {
        let toSave = props.coraSubjectSid

        toSave = {
          ...toSave,
          pecEmail: undefined,
          outgoingMailHost: undefined,
          outgoingMailPort: undefined,
          incomingMailHost: undefined,
          incomingMailPort: undefined,
          mailPassword: undefined,
          contactEmail: undefined,
          recipientEmail: undefined,
          incomingMailFolder: undefined,
          automaticElaborationEnabled: false
        }

        props.onSave(toSave)
      }
    })
  }

  /**
   * Esegue il test dei parametri email.
   *
   * Se la richiesta va a buon fine, nasconde il pulsante di test e mostra un alert di successo.
   * Se la richiesta fallisce, mostra un alert di errore con il messaggio di errore.
   */
  const testMailParameters = () => {
    props.setLoading(true)
    actions
      .testMailParameters(
        values.emailAddress,
        values.password,
        values.incomingMailHost,
        values.incomingMailPort,
        values.inFolder,
        values.outgoingMailHost,
        values.outgoingMailPort
      )
      .then(
        (res) => {
          props.setNeedsTest(false)
          notifySuccess(labels.MAIL_PARAMETERS_VERIFIED_SUCCESSFULLY)
        },
        (err) => {
          PopupError({ text: err })
        }
      )
      .then(() => props.setLoading(false))
  }

  /**
   * Salva la configurazione della PEC del soggetto selezionato.
   *
   * Recupera la configurazione attuale del soggetto selezionato e aggiorna i campi
   * relativi alla configurazione della PEC con i valori inseriti dall'utente.
   * Poi chiama la funzione onSave passandogli l'oggetto con i dati da salvare.
   */
  const onClickSave = () => {
    let toSave = props.coraSubjectSid

    toSave = {
      ...toSave,
      pecEmail: values.emailAddress,
      outgoingMailHost: values.outgoingMailHost,
      outgoingMailPort: values.outgoingMailPort,
      incomingMailHost: values.incomingMailHost,
      incomingMailPort: values.incomingMailPort,
      mailPassword: values.password,
      contactEmail: values.contactEmail,
      recipientEmail: values.recipientEmail,
      incomingMailFolder: values.inFolder,
      automaticElaborationEnabled: values.automaticElaborationEnabled
    }

    props.onSave(toSave)
  }

  /**
   * Gestisce il processo di salvataggio dei parametri PEC
   *
   * Se son presenti degli errori, mostra l'alert degli errori da correggere.
   * Altrimenti chiama la funzione di salvataggio passando i parametri del form da salvare
   *
   */
  const handleFormSubmit = async () => {
    if (props.archived) return
    setTouched(true)
    const errors = await validateForm(values) // Validazione dei valori
    if (Object.keys(errors).length > 0 || props.needsTest) {
      props.setShowErrorAlert(true) // Mostra l'alert degli errori
    } else {
      props.setShowErrorAlert(false) // Nascondi l'alert
      onClickSave(values)
    }
  }

  /**
   * Ritorna un blocco di testo che spiega le informazioni relative ai parametri PEC
   * Da una spiegazione dettagliata di ogni campo
   *
   * title: Titolo del campo
   * text: Testo di spiegazione
   *
   * @returns {JSX.Element} - ritorna il componente con i suggerimenti
   */
  const renderTipSection = () => {
    const tips = [
      {
        title: labels.OPERATOR,
        text: labels.SELECT_THE_MANAGER_OF_YOUR_PEC
      },
      {
        title: labels.PEC_SENDER_ADDRESS,
        text: labels.PEC_SENDER_ADDRESS_TEXT
      },
      {
        title: labels.PEC_RECIPIENT,
        text: labels.PEC_RECIPIENT_TEXT
      },
      {
        title: labels.INCOMING_MAIL_HOST,
        text: labels.INCOMING_MAIL_HOST_TEXT
      },
      {
        title: labels.INCOMING_MAIL_FOLDER,
        text: labels.INCOMING_MAIL_FOLDER_TEXT
      },
      {
        title: labels.CONTACT_EMAIL,
        text: labels.CONTACT_EMAIL_TEXT
      },
      {
        title: labels.ELABORATE_AUTOMATICALLY_RECEIPTS,
        text: labels.ELABORATE_AUTOMATICALLY_RECEIPTS_TEXT
      },
      {
        title: labels.LEGALMAIL_WARNING,
        aditionalInfoTitleClass: 'text-danger',
        text: labels.LEGALMAIL_WARNING_TEXT
      }
    ]

    return <InfoBox additionalClasses={'flex-grow-1 p-1 px-3 py-2'} simplePointsArr={tips} />
  }

  /**
   * Renderizza la sezione di configurazione della posta.
   *
   * @return {React.ReactElement}
   */
  const renderMailSection = () => {
    return (
      <div
        className="configurazioneSID d-flex flex-column justify-content-center"
        key={'mailsection-' + props.coraSubjectSid?.id}>
        <div className="row">
          <div className="col-12 mb-2 ps-2 pe-2 pt-2">
            {/* Indirizzo PEC Mittente */}
            <BwmInput
              disabled={props.archived}
              className={'form-control'}
              label={labels.PEC_SENDER_ADDRESS}
              value={values.emailAddress}
              error={errors.emailAddress}
              touched={touched}
              onChange={(e) => handleChangeTextField(e.target.value.toUpperCase(), 'emailAddress')}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-12 mb-2 ps-2 pe-2">
            <div className="d-flex">
              <div style={{ flexGrow: 1 }}>
                {/* Password */}
                <BwmInput
                  disabled={props.archived}
                  className={'form-control'}
                  label={labels.PASSWORD}
                  type={passwordFieldType}
                  name={'passwordPec'}
                  value={values.password}
                  error={errors.password}
                  touched={touched}
                  Icon={() => (
                    // Serve per mostrare la password come tipo 'password' o come tipo 'testo'
                    <div
                      className={`align-self-center mt-2 ms-2 cursor-pointer ${errors.password && touched ? 'me-4' : ''}`}
                      onClick={() => onClickPwdEye()}>
                      <i
                        className={`star-icon bg-primary ${passwordFieldType === 'text' ? 'star-icon-eye' : 'star-icon-eye-off'}`}
                      />
                    </div>
                  )}
                  onChange={(e) => handleChangeTextField(e.target.value, 'password')}
                  autoComplete={'new-password'}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-12 mb-2 ps-2 pe-2">
            {/* Indirizzo Pec DESTINATARIO */}
            <BwmInput
              disabled={props.archived}
              className={'form-control'}
              label={labels.PEC_RECIPIENT}
              value={values.recipientEmail}
              error={errors.recipientEmail}
              touched={touched}
              onChange={(e) => {
                handleChangeTextField(e.target.value.toUpperCase(), 'recipientEmail')
              }}
            />
          </div>
        </div>
      </div>
    )
  }

  /**
   * Renderizza la sezione di configurazione dei parametri di posta.
   * La sezione  suddivisa in due colonne, una per la configurazione della posta in entrata e l'altra per la configurazione della posta in uscita.
   *
   * @returns {React.ReactElement} - ritorna il componente con i parametri di configurazione della posta
   */
  const renderMailParamsSection = () => {
    return (
      <div key={'mailParamssection-' + props.coraSubjectSid?.id}>
        <div className="row justify-content-center mb-2">
          <div className="col-6 pe-2">
            <div className="configurazioneSID p-2">
              {/* Server posta in entrata */}
              <div>
                <BwmInput
                  disabled={props.archived}
                  className={'form-control'}
                  label={labels.INCOMING_MAIL_HOST}
                  value={values.incomingMailHost}
                  error={errors.incomingMailHost}
                  touched={touched}
                  onChange={(e) => handleChangeTextField(e.target.value, 'incomingMailHost')}
                />
              </div>
              <div className="row">
                <div className="col-4 pe-2 mt-2">
                  {/* Porta */}
                  <BwmInput
                    disabled={props.archived}
                    className={'form-control'}
                    label={labels.PORT}
                    value={values.incomingMailPort}
                    error={errors.incomingMailPort}
                    touched={touched}
                    onChange={(e) => handleChangeNumericField(e.target.value, 'incomingMailPort')}
                  />
                </div>
                <div className="col-8 mt-2">
                  <div className="d-flex">
                    <div style={{ flexGrow: 1 }}>
                      {/* Casella di posta in entrata */}
                      <BwmInput
                        disabled={props.archived}
                        className={'form-control'}
                        label={labels.INCOMING_MAIL_FOLDER}
                        value={values.inFolder}
                        error={errors.inFolder}
                        touched={touched}
                        onChange={(e) => handleChangeTextField(e.target.value, 'inFolder')}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-6">
            <div className="configurazioneSID p-2">
              {/* Server posta in uscita */}
              <div className="mb-2">
                <BwmInput
                  disabled={props.archived}
                  className={'form-control'}
                  label={labels.OUTGOING_MAIL_HOST}
                  value={values.outgoingMailHost}
                  error={errors.outgoingMailHost}
                  touched={touched}
                  onChange={(e) => handleChangeTextField(e.target.value, 'outgoingMailHost')}
                />
              </div>
              <div className="row">
                <div className="col-4">
                  {/* Porta */}
                  <BwmInput
                    disabled={props.archived}
                    className={'form-control'}
                    label={labels.PORT}
                    value={values.outgoingMailPort}
                    error={errors.outgoingMailPort}
                    touched={touched}
                    onChange={(e) => handleChangeNumericField(e.target.value, 'outgoingMailPort')}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  /**
   * Renderizza la sezione relativa al servizio di posta elettronica.
   *
   * @returns {React.ReactElement} - il componente con la sezione del servizio di posta elettronica.
   */
  const renderServiceSection = () => {
    return (
      <div className="row">
        <div
          className="configurazioneSID p-2 mb-2"
          key={'servicesection-' + props.coraSubjectSid?.id}>
          <div className="row">
            <div className="col-8 pe-2">
              <div className="d-flex">
                <div style={{ flexGrow: 1 }}>
                  {/* Indirizzo di posta ordinario per contatti */}
                  <BwmInput
                    disabled={props.archived}
                    className={'form-control'}
                    label={labels.CONTACT_EMAIL}
                    value={values.contactEmail}
                    error={errors.contactEmail}
                    touched={touched}
                    onChange={(e) => handleChangeTextField(e.target.value, 'contactEmail')}
                  />
                </div>
              </div>
            </div>
            <div className="col d-flex">
              <div className={'align-self-center'}>
                {/* Elabora automaticamente le ricevute */}
                <BwmCheckbox
                  disabled={props.archived}
                  name={'automaticElaborationEnabled'}
                  label={labels.ELABORATE_AUTOMATICALLY_RECEIPTS}
                  checked={values.automaticElaborationEnabled}
                  error={errors.automaticElaborationEnabled}
                  touched={touched}
                  onChange={(e) => handleAutoElaborationCheckBox(e.target.checked)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  /**
   * Renderizza la sezione relativa alle condizioni di accettazione
   * del servizio di posta elettronica.
   *
   * @returns {React.ReactElement} - il componente con la sezione
   * delle condizioni di accettazione.
   */
  const renderEULASection = () => {
    return (
      <div className="row">
        <div
          className="configurazioneSID mt-1 pt-1 pb-1 "
          key={'servicesection-' + props.coraSubjectSid?.id}>
          <div className="row">
            <div className="col-10 text-small p-1 px-3">
              {/* Condizioni di utilizzo */}
              <small>
                {labels.ACCEPT_CONDITIONS_TEXT}
                <br />
                {labels.ACCEPT_CONDITIONS_TEXT_2}
              </small>
            </div>
            <div className="col-2 d-flex align-items-center justify-content-center">
              {/* Pulsante di accettazione */}
              <button
                disabled={props.archived}
                className={
                  !values.needsToAccept ? 'btn btn-outline-primary ' : 'btn btn-outline-primary '
                }
                onClick={() => handleCheckbox(!values.needsToAccept)}>
                {labels.ACCEPT}
                {!values.needsToAccept ? (
                  <i className="star-icon-toggle-off star-icon ms-2" />
                ) : (
                  <i className="star-icon-toggle-on star-icon ms-2" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <div>
        <div className="row">
          <div className="w-37 d-flex flex-column">
            <div className="row">
              <div className="col-12 mb-2 pe-2">
                {/* Operatore predefinito */}
                <BwmSelect
                  disabled={props.archived}
                  options={
                    props.mailPresets
                      ? props.mailPresets.map((p) => {
                          return { id: p.id, description: p.name }
                        })
                      : []
                  }
                  name="select"
                  label={labels.SELECT_OPERATOR}
                  optionValue="id"
                  optionLabel="description"
                  value={operatorSelect}
                  onChange={(value) => handleChangeMailPreset(value)}
                />
              </div>
            </div>
            <div className="row flex-grow-1 mb-2 pe-2">{renderMailSection()}</div>
          </div>
          <div className="w-63 mb-2 d-flex flex-column">{renderTipSection()}</div>
        </div>

        {'/* Reindirizza le varie sezioni del form */'}
        {renderMailParamsSection()}
        {renderServiceSection()}
        {renderEULASection()}
      </div>
      <div className="row pt-3 justify-content-between align-items-center">
        {/* Pulsanti modali, se soggetto archiviato vengono disabilitati */}
        <div className="col-2">
          {/* Pulsante di annullamento (Chiusura modale) */}
          <button className="btn btn-outline-primary " onClick={() => props.onCancel()}>
            <i className="star-icon-cancel star-icon me-2" />
            {labels.CANCEL}
          </button>
        </div>
        <div className="col-4">
          {/* Pulsante per eliminare la configurazione */}
          <button
            className="btn btn-outline-primary ms-2 btn-outline-primary"
            onClick={() => onClean()}>
            {labels.DELETE_CONFIGURATION}
          </button>
        </div>
        <div className="col-3 d-flex justify-content-center">
          {/* Pulsante che controlla la connessione */}
          <button
            disabled={props.archived}
            className="btn btn-outline-primary"
            onClick={() => handleFormVerify()}>
            {labels.CHECK_CONNECTION}
          </button>
        </div>
        <div className="col-3 d-flex justify-content-end">
          {/* Pulsante per salvare la configurazione dei parametri pec */}
          <button
            disabled={props.archived}
            className="btn btn-primary ml-2"
            onClick={() => handleFormSubmit()}>
            <i className="star-icon-floppy-disk star-icon me-2" />
            {labels.SAVE}
          </button>
        </div>
      </div>
    </>
  )
}
